import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { default as NukaCarousel, CarouselSlideRenderControlProps } from 'nuka-carousel';
import Lightbox from 'react-image-lightbox';
import { useWidth } from 'common/hooks';
import { makeCarouselStyles, makeNavigateIconStyles } from 'styles/components/carousel';
import arrowRightIcon from 'assets/icons/arrow_right_icon.svg';
import 'react-image-lightbox/style.css';
import { SIZES } from 'views/pages/Product/common/constants';

type Image = {
    absolutePath: string;
    size: string;
};

type Props = {
    flipbook?: {
        image: string;
        url: string;
    };
    gallery: Array<{
        path: string;
        sizes: Array<Image>;
    }>;
};

enum BREAKPOINTS {
    MD = 'md',
    LG = 'lg',
    XL = 'xl',
}

enum DIRECTION {
    LEFT = 'left',
    RIGHT = 'right',
}

const Flipbook: React.FC<{
    data?: {
        image: string;
        url: string;
    };
}> = ({ data }) => {
    const { arrow, flipbookLink, image } = makeCarouselStyles();

    return (
        <a className={flipbookLink} href={data?.url} target="_blank" rel="noopener noreferrer">
            <img alt="" className={image} src={data?.image} />
            <img alt="" className={arrow} src={arrowRightIcon} />
        </a>
    );
};

const Carousel: React.FC<Props> = ({ flipbook, gallery }) => {
    const [isOpen, setOpen] = useState(false);
    const [photoIndex, setIndex] = useState(0);

    const currentWidth = useWidth();
    const breakpoints: Array<string> = [BREAKPOINTS.MD, BREAKPOINTS.LG, BREAKPOINTS.XL];
    const isDesktop = breakpoints.includes(currentWidth);

    const navigateIconClasses = makeNavigateIconStyles();
    const { carouselWrapper, iconBefore, iconNext, image, title } = makeCarouselStyles();

    const filterGallery = (selectedSize: string): Array<Image> => {
        return [].concat
            .apply(
                [],
                gallery.map(({ sizes }: any) => sizes),
            )
            .filter(({ size }: Image) => size === selectedSize);
    };

    const galleryFilteredThumbails: Array<Image> = filterGallery(SIZES.MEDIUM);
    const galleryFilteredOriginals: Array<Image> = filterGallery(SIZES.ORIGINAL);

    const prevImage =
        (galleryFilteredThumbails && (photoIndex + galleryFilteredThumbails.length - 1) % galleryFilteredThumbails.length) ||
        0;
    const nextImage = (galleryFilteredThumbails && (photoIndex + 1) % galleryFilteredThumbails.length) || 0;

    const handleImageClick = (index: number) => {
        if (!isDesktop) {
            return;
        }

        setIndex(index);
        setOpen(true);
    };

    const renderControls = (direction: DIRECTION.LEFT | DIRECTION.RIGHT) => (
        props: CarouselSlideRenderControlProps,
    ) => {
        if (props.slideCount <= 1) return null;

        if (direction === DIRECTION.LEFT) {
            return (
                <NavigateBeforeIcon
                    className={iconBefore}
                    classes={navigateIconClasses}
                    onClick={props.previousSlide}
                />
            );
        }

        return (
            <NavigateNextIcon
                className={iconNext}
                classes={navigateIconClasses}
                onClick={props.nextSlide}
            />
        );
    };

    return gallery.length || flipbook ? (
        <>
            <Box className={carouselWrapper}>
                <Typography className={title}>{flipbook ? 'Zobacz fragment książki' : 'Zobacz galerię'}</Typography>
                <NukaCarousel
                    dragging={false}
                    heightMode="current"
                    renderBottomCenterControls={null}
                    renderCenterLeftControls={renderControls(DIRECTION.LEFT)}
                    renderCenterRightControls={renderControls(DIRECTION.RIGHT)}
                    wrapAround
                >
                    {flipbook && <Flipbook data={flipbook} />}
                    {galleryFilteredThumbails?.map(({ absolutePath }, index) => (
                        <img
                            key={index}
                            alt=""
                            className={image}
                            onClick={() => handleImageClick(index)}
                            src={absolutePath}
                        />
                    ))}
                </NukaCarousel>
            </Box>
            {isOpen && isDesktop && galleryFilteredOriginals ? (
                <Lightbox
                    enableZoom={false}
                    mainSrc={galleryFilteredOriginals[photoIndex].absolutePath}
                    nextSrc={galleryFilteredOriginals[nextImage].absolutePath}
                    prevSrc={galleryFilteredOriginals[prevImage].absolutePath}
                    onCloseRequest={() => setOpen(false)}
                    onMoveNextRequest={() => setIndex(nextImage)}
                    onMovePrevRequest={() => setIndex(prevImage)}
                />
            ) : null}
        </>
    ) : null;
};

export default Carousel;
